import type { GroupSeatStatus } from '../groupSubscriptionTypes';

export type RowActionCallbacks = {
  onResendInvite?: () => void;
  onCancelInvite?: () => void;
  onReinvite?: () => void;
  onRemove?: () => void;
  /** Terminating seats only: reopen the scheduled-reduction picker so the admin
   *  can swap which members are set to lose access at renewal. */
  onManageReduction?: () => void;
};

type RowActionsProps = {
  status: GroupSeatStatus;
  callbacks: RowActionCallbacks;
};

export function RowActions({ status, callbacks }: RowActionsProps) {
  if (status === 'invited') {
    return (
      <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-center">
        <button
          type="button"
          onClick={callbacks.onResendInvite}
          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Resend invite
        </button>
        <button
          type="button"
          onClick={callbacks.onCancelInvite}
          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-red-600 shadow-sm hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    );
  }

  if (status === 'removed' || status === 'declined') {
    return (
      <button
        type="button"
        onClick={callbacks.onReinvite}
        className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
      >
        Reinvite
      </button>
    );
  }

  // `unsubscribed` is a terminal opt-out from the publication entirely (not
  // just this seat) — Rails rejects reinvites against it for subscriber-
  // autonomy reasons, so we render no action rather than offer a button that'd
  // fail at submit. See BULK_ACTIONABLE_STATUSES in groupCard.utils.
  if (status === 'unsubscribed') {
    return null;
  }

  // `terminating` = scheduled to lose access at renewal. "Manage" reopens the
  // picker to swap who's losing access; "Remove" still pulls the seat now.
  if (status === 'terminating') {
    return (
      <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-center">
        <button
          type="button"
          onClick={callbacks.onManageReduction}
          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Manage
        </button>
        <button
          type="button"
          onClick={callbacks.onRemove}
          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-red-600 shadow-sm hover:bg-gray-50"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={callbacks.onRemove}
      className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-red-600 shadow-sm hover:bg-gray-50"
    >
      Remove
    </button>
  );
}
